import * as React from "react";
import { useState, useEffect } from "react";

import { getSearchProvider } from "@src/providers";

import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import { BlogArticleTile } from "@project-stories/blog/BlogArticleTile/BlogArticleTile";
import { LoadingSpinner } from "@arctheme-components/elements/LoadingSpinner/LoadingSpinner";

const searchEvent = 'searchrequest-products-complete';
const pillEvent = 'search-results-pill-clicked';
const type = "search";
let hasLoaded = false;

export const SearchResults = ({ settings }) => {
  const params = new URLSearchParams(window.location.search);
  const query = params.get("q");

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [activePill, setActivePill] = useState('products');

  useEffect(() => {
    if (hasLoaded) return;

    const handleSearchResults = (event) => {
      if (event?.detail?.type !== type) return;

      setProducts(event.detail.products || []);
      setArticles(event.detail.articles || []);
      setLoading(false);
    };

    const handlePillClick = (event) => {
      setActivePill(event.detail);
    }

    // Set up event listeners
    window.addEventListener(searchEvent, handleSearchResults);
    window.addEventListener(pillEvent, handlePillClick);

    // Dispatch search event
    window.dispatchEvent(
      new CustomEvent("searchrequest", {
        detail: { query, type }
      })
    );

    hasLoaded = true;

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener(searchEvent, handleSearchResults);
      window.removeEventListener(pillEvent, handlePillClick);
    };
  }, []);

  // Determine which search provider to use
  const SearchProviderComponent = getSearchProvider(settings.search_system || 'shopify');

  return (
    <SearchProviderComponent settings={settings} searchType="search">
      <div>
        {loading && <LoadingSpinner id="spinner-search" />}

        <div className={`flex ${activePill === 'products' ? 'flex-col' : 'flex-col-reverse'} gap-8`}>
          {(!loading && !!products.length) && (
            <div>
              <h3 className="mb-5">
                {settings.section.products_heading || 'Shop Results'}
              </h3>

              <div
                data-id={settings.section.id}
                id="product-grid"
                className={`
                  search product-grid grid
                  
                  grid-cols-${settings.section.columns_mobile}
                  lg:grid-cols-${settings.section.columns_desktop}
                  
                  gap-x-[${settings.section.column_gap_mobile}px]
                  lg:gap-x-[${settings.section.column_gap_desktop}px]
                  
                  gap-y-[${settings.section.row_gap_mobile}px]
                  lg:gap-y-[${settings.section.row_gap_desktop}px]

                  auto-cols-max
                `}
              >
                {products.map((product, index) => (
                  <div
                    key={`${product.id}_${index}`}
                    className={"search-tile"}
                  >
                    <ProductTile
                      settings={settings}
                      product={product}
                      siblings={undefined}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!loading && !!articles.length) && (
            <div>
              <h3 className="mb-5">
                {settings.section.articles_heading || 'Blog Results'}
              </h3>

              <div
                data-id={settings.section.id}
                id="articles-grid"
                className={`
                  search article-grid grid
                  
                  grid-cols-${settings.section.articles_columns_mobile}
                  lg:grid-cols-${settings.section.articles_columns_desktop}
                  
                  gap-x-[${settings.section.articles_column_gap_mobile}px]
                  lg:gap-x-[${settings.section.articles_column_gap_desktop}px]
                  
                  gap-y-[${settings.section.articles_row_gap_mobile}px]
                  lg:gap-y-[${settings.section.articles_row_gap_desktop}px]

                  auto-cols-max
                `}
              >
                {articles.map((article, index) => (
                  <div
                    key={`${article.id}_${index}`}
                    className={"search-tile"}
                  >
                    <BlogArticleTile
                      article={article}
                      settings={settings}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {products.length <= 0 && articles.length <= 0 && !!query?.length && !loading && (
          <h6 className="text-center py-20">
            Unfortunately, nothing matches your search. Please try another
            search term.
          </h6>
        )}
      </div>
    </SearchProviderComponent>
  );
};

export default SearchResults;
